import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Filters, SORT_OPTIONS } from "@/components/Filters";
import { GridSkeleton, ProductGrid } from "@/components/ProductCard";
import { FaqList } from "@/components/FaqList";
import { CtaBand } from "@/components/CtaBand";
import { DealsFlyer } from "@/components/DealsFlyer";
import { fetcher, swrOpts } from "@/lib/api";
import { breadcrumbLd, faqLd } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

const PAGE = 24;
const FILTER_KEYS = ["q", "brand", "strain", "min_price", "max_price", "in_stock", "on_sale", "sort"];
const ALL_SEO = {
  title: "Shop Cannabis Online in Washington, DC | Full Menu | SHADC Dispensary",
  meta_description: "Shop the full SHADC menu — flower, pre-rolls, edibles, vapes, cartridges and concentrates — with delivery across Washington, DC. Adults 21+.",
  h1: "Shop All Cannabis Products",
  intro: "Browse the complete SHADC menu. Filter by category, brand, strain type, price and availability.",
};

const SeoBody = ({ seo, related }) => (
  <section className="container-x mt-20 grid gap-12 lg:grid-cols-[1.6fr_1fr]">
    <div className="rich max-w-3xl">
      {(seo.sections || []).map((s) => (
        <div key={s.h2}><h2>{s.h2}</h2>{s.paragraphs.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}</div>
      ))}
      {seo.faqs?.length > 0 && <><h2>Questions about {seo.h1.toLowerCase()}</h2><div className="not-prose mt-4"><FaqList faqs={seo.faqs} id="category-faq" /></div></>}
    </div>
    <aside className="space-y-8">
      {related?.length > 0 && (
        <div className="card-surface p-6">
          <h2 className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-gold">Related categories</h2>
          <ul className="mt-4 space-y-2">{related.map((r) => <li key={r}><Link to={r === "deals" ? "/deals/" : `/shop/${r}/`} className="text-gray-200 hover:text-gold">{r.replace("-", " ").replace(/\b\w/g, (m) => m.toUpperCase())} →</Link></li>)}</ul>
        </div>
      )}
      <div className="card-surface p-6">
        <h2 className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-gold">Helpful guides</h2>
        <ul className="mt-4 space-y-2 text-gray-200">
          <li><Link to="/how-to-order/" className="hover:text-gold">How to order →</Link></li>
          <li><Link to="/delivery/" className="hover:text-gold">Delivery details →</Link></li>
          <li><Link to="/blog/" className="hover:text-gold">Cannabis guides →</Link></li>
        </ul>
      </div>
    </aside>
  </section>
);

export default function Shop({ deals = false }) {
  const params = useParams();
  const slug = deals ? "deals" : params.category;
  const [sp, setSp] = useSearchParams();
  const [pages, setPages] = useState(1);
  const [mobileFilters, setMobileFilters] = useState(false);
  const { data: cat, error: catErr } = useSWR(slug ? `/categories/${slug}` : null, fetcher, swrOpts);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    if (slug) q.set("category", slug);
    FILTER_KEYS.forEach((k) => sp.get(k) && q.set(k, sp.get(k)));
    q.set("limit", String(PAGE * pages));
    return q.toString();
  }, [slug, sp, pages]);
  const { data, isLoading } = useSWR(`/products?${query}`, fetcher, swrOpts);

  if (catErr?.response?.status === 404) return <NotFound />;
  const set = (k, v) => {
    const next = new URLSearchParams(sp);
    Object.entries(typeof k === "object" ? k : { [k]: v }).forEach(([kk, vv]) => (vv ? next.set(kk, vv) : next.delete(kk)));
    setPages(1);
    setSp(next, { replace: true });
  };

  const seo = slug ? cat?.seo || {} : ALL_SEO;
  const path = deals ? "/deals/" : slug ? `/shop/${slug}/` : "/shop/";
  const name = slug ? cat?.name || "" : "Shop All";
  const crumbs = [{ name: "Home", path: "/" }, ...(slug ? [{ name: "Shop", path: "/shop/" }] : []), { name: slug ? name : "Shop", path }];
  const q = sp.get("q");
  const h1 = q ? `Search results for “${q}”` : seo.h1 || name;

  return (
    <>
      <Seo title={q ? `Search: ${q} | SHADC Dispensary` : seo.title || `${name} | SHADC Dispensary`}
        description={seo.meta_description || `Shop ${name} at SHADC Dispensary in Washington, DC.`} path={path}
        noindex={Boolean(q) || [...sp.keys()].length > 0} jsonLd={[breadcrumbLd(crumbs), faqLd(seo.faqs)]} />
      <div className="border-b border-line bg-panel/40">
        <div className="container-x py-10 sm:py-14">
          <Breadcrumbs items={crumbs} />
          <h1 className="mt-5 text-4xl font-bold text-white sm:text-5xl" data-testid="shop-title">{h1}</h1>
          {!q && seo.intro && <p className="mt-4 max-w-2xl text-base text-gray-300">{seo.intro}</p>}
          {!slug && (
            <ul className="no-scrollbar mt-7 flex gap-2 overflow-x-auto pb-1" aria-label="Categories">
              {["flower", "pre-rolls", "edibles", "vapes", "cartridges", "concentrates"].map((c) => (
                <li key={c}><Link to={`/shop/${c}/`} className="chip whitespace-nowrap capitalize" data-testid={`shop-category-chip-${c}`}>{c.replace("-", " ")}</Link></li>
              ))}
              <li><Link to="/deals/" className="chip whitespace-nowrap" data-testid="shop-category-chip-deals">Deals</Link></li>
            </ul>
          )}
        </div>
      </div>

      {deals && !q && <DealsFlyer />}
      <div id="deal-products" className="container-x mt-8 grid scroll-mt-28 gap-10 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block" aria-label="Filters"><Filters facets={data?.facets} sp={sp} set={set} hideSale={deals} /></aside>
        <div>
          <div className="mb-6 flex items-center justify-between gap-3">
            <p className="text-sm text-gray-400" aria-live="polite" data-testid="shop-result-count">{data ? `${data.total} products` : "Loading…"}</p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setMobileFilters(true)} className="chip lg:hidden" data-testid="mobile-filters-button"><SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden />Filters</button>
              <label htmlFor="sort" className="sr-only">Sort products</label>
              <select id="sort" value={sp.get("sort") || "featured"} onChange={(e) => set("sort", e.target.value === "featured" ? "" : e.target.value)}
                className="h-10 rounded-full border border-line bg-panel px-4 text-sm text-gray-200" data-testid="sort-select">
                {SORT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          {isLoading && !data ? <GridSkeleton /> : data?.items.length ? <ProductGrid items={data.items} eagerCount={4} /> : (
            <div className="card-surface p-10 text-center" data-testid="shop-empty"><p className="text-lg text-white">No products match these filters.</p>
              <button type="button" onClick={() => setSp({}, { replace: true })} className="btn-ghost mt-6">Reset filters</button></div>
          )}
          {data && data.items.length < data.total && (
            <div className="mt-10 text-center"><button type="button" onClick={() => setPages((p) => p + 1)} className="btn-ghost" data-testid="shop-load-more-button">Load more ({data.total - data.items.length} remaining)</button></div>
          )}
        </div>
      </div>

      <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl border-line bg-ink p-6 text-gray-100" data-testid="mobile-filters-sheet">
          <SheetTitle className="mb-5 font-display text-2xl text-white">Filters</SheetTitle>
          <Filters facets={data?.facets} sp={sp} set={set} hideSale={deals} />
          <button type="button" onClick={() => setMobileFilters(false)} className="btn-gold mt-6 w-full" data-testid="mobile-filters-apply">Show {data?.total ?? ""} products</button>
        </SheetContent>
      </Sheet>

      {!q && slug && <SeoBody seo={seo} related={seo.related} />}
      <CtaBand />
    </>
  );
}
